import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, X, Send, Copy, Check, Trash2, Bot } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfig } from "@/lib/useConfig";

function getSessionId() {
  let id = localStorage.getItem("wai_assistant_session");
  if (!id) {
    id = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("wai_assistant_session", id);
  }
  return id;
}

const timeNow = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function MarkdownBody({ text }) {
  const navigate = useNavigate();
  return (
    <div className="ai-md text-sm leading-relaxed space-y-2">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            const internal = href && href.startsWith("/");
            return (
              <a href={href}
                onClick={(e) => { if (internal) { e.preventDefault(); navigate(href); } }}
                target={internal ? undefined : "_blank"} rel="noreferrer"
                className="text-[#00F0FF] underline underline-offset-2 hover:text-[#5ef7ff]">{children}</a>
            );
          },
          table: ({ children }) => <table className="w-full text-xs border border-white/10 rounded my-2">{children}</table>,
          th: ({ children }) => <th className="text-left px-2 py-1 bg-white/5 border-b border-white/10">{children}</th>,
          td: ({ children }) => <td className="px-2 py-1 border-b border-white/5">{children}</td>,
          ul: ({ children }) => <ul className="list-disc pl-4 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-4 space-y-1">{children}</ol>,
          h2: ({ children }) => <h4 className="font-display font-bold text-base mt-1">{children}</h4>,
          h3: ({ children }) => <h5 className="font-semibold text-sm mt-1">{children}</h5>,
          code: ({ children }) => <code className="bg-white/10 px-1 rounded text-[#5ef7ff]">{children}</code>,
        }}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

function AssistantMessage({ m }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(m.content); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="group flex gap-2" data-testid="assistant-message">
      <div className="w-7 h-7 rounded-full bg-[#00F0FF]/15 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-[#00F0FF]" /></div>
      <div className="flex-1 min-w-0">
        <div className="bg-[#111a2e] border border-white/10 rounded-2xl rounded-tl-sm px-3.5 py-2.5">
          <MarkdownBody text={m.content} />
        </div>
        <div className="flex items-center gap-2 mt-1 pl-1">
          <span className="text-[10px] text-[#64748B] font-mono-tech">{m.time}</span>
          <button onClick={copy} data-testid="assistant-copy-btn" className="opacity-0 group-hover:opacity-100 transition text-[#64748B] hover:text-[#00F0FF]">
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ welcome: "", suggested: [] });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [limited, setLimited] = useState(false);
  const { paymentsEnabled } = useConfig();
  const scrollRef = useRef();
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (open && !meta.welcome) {
      api.get("/assistant/meta").then((r) => setMeta(r.data)).catch(() => {});
    }
  }, [open, meta.welcome]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q, time: timeNow() }]);
    setBusy(true);
    try {
      const r = await api.post("/assistant/chat", { session_id: sessionId.current, message: q });
      if (r.data.limited) {
        setLimited(true);
        setMessages((m) => [...m, { role: "assistant", content: r.data.reply, time: timeNow() }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: r.data.reply, time: timeNow() }]);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Assistant is unavailable right now.");
    } finally { setBusy(false); }
  };

  const clearConversation = () => { setMessages([]); setLimited(false); };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <motion.button
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          onClick={() => setOpen(true)} data-testid="ai-assistant-fab"
          className="group fixed bottom-5 right-5 z-[70] w-14 h-14 rounded-full bg-[#00F0FF] text-[#04121a] shadow-[0_0_24px_rgba(0,240,255,0.5)] flex items-center justify-center hover:brightness-110">
          <Sparkles className="w-6 h-6" />
          <span className="absolute right-16 whitespace-nowrap bg-[#0B0F19] border border-white/10 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition pointer-events-none">
            Ask AI anything about the AI world
          </span>
        </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-assistant-panel"
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className="fixed z-[70] bg-[#0B0F19] border-[#00F0FF]/20 flex flex-col
                       md:bottom-5 md:right-5 md:w-[400px] md:h-[600px] md:max-h-[80vh] md:rounded-2xl md:border
                       inset-0 md:inset-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#00F0FF]/15 flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#00F0FF]" /></div>
                <div>
                  <div className="font-display font-bold text-sm leading-none">WorldAIInfo Assistant</div>
                  <div className="text-[10px] text-[#4ade80] font-mono-tech mt-0.5">● Online</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={clearConversation} data-testid="ai-assistant-clear" title="Clear conversation" className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-[#94A3B8]"><Trash2 className="w-4 h-4" /></button>
                <button onClick={() => setOpen(false)} data-testid="ai-assistant-close" className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-[#94A3B8]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Welcome */}
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-[#00F0FF]/15 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-[#00F0FF]" /></div>
                <div className="bg-[#111a2e] border border-white/10 rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm">
                  {meta.welcome || "Hi, I am the WorldAIInfo AI Assistant. Ask me anything about AI tools, jobs, courses, events, or the global AI industry."}
                </div>
              </div>
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pl-9" data-testid="ai-assistant-suggestions">
                  {(meta.suggested || []).map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="ai-suggestion-chip"
                      className="text-xs px-3 py-1.5 rounded-full border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition">{s}</button>
                  ))}
                </div>
              )}

              {messages.map((m, i) => m.role === "assistant"
                ? <AssistantMessage key={i} m={m} />
                : (
                  <div key={i} className="flex justify-end" data-testid="user-message">
                    <div className="max-w-[85%]">
                      <div className="bg-[#00F0FF] text-[#04121a] rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm font-medium">{m.content}</div>
                      <div className="text-[10px] text-[#64748B] font-mono-tech mt-1 text-right pr-1">{m.time}</div>
                    </div>
                  </div>
                ))}

              {busy && (
                <div className="flex gap-2" data-testid="ai-typing">
                  <div className="w-7 h-7 rounded-full bg-[#00F0FF]/15 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-[#00F0FF]" /></div>
                  <div className="bg-[#111a2e] border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1">
                    {[0, 1, 2].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
                  </div>
                </div>
              )}

              {limited && (
                <div className="rounded-xl border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 p-4 text-center" data-testid="ai-limit-prompt">
                  {paymentsEnabled ? (
                    <>
                      <p className="text-sm text-white/90">Upgrade to <span className="font-bold text-[#a78bfa]">Premium</span> for unlimited questions.</p>
                      <p className="text-xs text-[#94A3B8] mt-1">$9/month · unlimited AI assistance</p>
                      <button onClick={() => toast.info("Premium plans launching soon!")} data-testid="ai-upgrade-btn"
                        className="mt-3 bg-[#8B5CF6] text-white font-semibold text-sm px-5 py-2 rounded-md hover:brightness-110">Upgrade to Premium</button>
                    </>
                  ) : (
                    <p className="text-sm text-white/90">You've reached today's free questions. Come back tomorrow for more — it's free while we grow.</p>
                  )}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-white/10 p-3 shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1} placeholder="Ask about AI tools, jobs, courses…" data-testid="ai-assistant-input"
                  className="flex-1 resize-none bg-[#111a2e] border border-white/10 rounded-xl px-3 py-2.5 text-sm max-h-28 focus:border-[#00F0FF] focus:outline-none" />
                <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="ai-assistant-send"
                  className="w-10 h-10 rounded-xl bg-[#00F0FF] text-[#04121a] flex items-center justify-center disabled:opacity-40 hover:brightness-110 shrink-0"><Send className="w-4 h-4" /></button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
